import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, filter, map } from 'rxjs';

import { environment } from '@environments/environment';
import { FirebaseAuthClientService } from '../firebase/firebase-auth-client.service';
import { FirebaseProfileService } from '../firebase/firebase-profile.service';
import { ProjectContextService } from '../project-context/project-context.service';
import { safeLocalStorageGetItem, safeLocalStorageRemoveItem, safeLocalStorageSetItem } from '../../utils/storage';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Demo mode persistence key (used only when environment.authProvider === 'demo').
  private readonly DEMO_STORAGE_KEY = 'everest.auth.demo.v1';
  private readonly LAST_ERROR_STORAGE_KEY = 'everest.auth.last-error.v1';

  private readonly loggedInSubject = new BehaviorSubject<boolean | undefined>(undefined);
  readonly isLoggedIn$: Observable<boolean | undefined> = this.loggedInSubject.asObservable();

  private readonly subs = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly firebaseAuth: FirebaseAuthClientService,
    private readonly firebaseProfile: FirebaseProfileService,
    private readonly projectContext: ProjectContextService
  ) {
    if ((environment as any).authProvider === 'demo') {
      const raw = safeLocalStorageGetItem(this.DEMO_STORAGE_KEY);
      this.loggedInSubject.next(raw === '1');
      return;
    }

    this.subs.add(
      this.firebaseAuth.user$.pipe(filter((u) => typeof u !== 'undefined')).subscribe(async (user) => {
        this.loggedInSubject.next(!!user);
        if (!user) {
          this.projectContext.clear();
          return;
        }

        try {
          const profile = await this.firebaseProfile.getUsuarioAdmin(user.uid);
          if (!profile) {
            await this.firebaseAuth.signOut();
            this.projectContext.clear();
            this.storeLastAuthError('login.errors.not-authorized');
            this.router.navigate(['/login']);
            return;
          }

          if (profile.activo === false) {
            await this.firebaseAuth.signOut();
            this.projectContext.clear();
            this.storeLastAuthError('login.errors.inactive-user');
            this.router.navigate(['/login']);
            return;
          }

          const allowed = this.extractAllowedPlatformIds(profile);
          if (allowed.length === 0) {
            await this.firebaseAuth.signOut();
            this.projectContext.clear();
            this.storeLastAuthError('login.errors.no-projects');
            this.router.navigate(['/login']);
            return;
          }

          this.projectContext.setAllowedPlatformIds(allowed);
        } catch (err) {
          // Most commonly: Firestore permission denied for USUARIOSADMIN.
          try {
            // eslint-disable-next-line no-console
            console.warn('[AUTH] Failed to load UsuarioAdmin profile', err);
          } catch {
            // ignore
          }

          await this.firebaseAuth.signOut();
          this.projectContext.clear();
          this.storeLastAuthError('login.errors.not-authorized');
          this.router.navigate(['/login']);
        }
      })
    );
  }

  async login(usernameOrEmail: string, password: string): Promise<boolean> {
    const result = await this.loginDetailed(usernameOrEmail, password);
    return result.ok;
  }

  async loginDetailed(
    usernameOrEmail: string,
    password: string
  ): Promise<{ ok: true } | { ok: false; messageKey: string }> {
    if ((environment as any).authProvider === 'demo') {
      // Backward compatible local login for E2E / offline dev.
      if (usernameOrEmail === 'demo' && password === 'demo') {
        safeLocalStorageSetItem(this.DEMO_STORAGE_KEY, '1');
        this.loggedInSubject.next(true);
        return { ok: true };
      }
      this.loggedInSubject.next(false);
      return { ok: false, messageKey: 'login.errors.invalid-credentials' };
    }

    const email = (usernameOrEmail || '').trim();
    if (!email || !password) {
      this.loggedInSubject.next(false);
      return { ok: false, messageKey: 'login.errors.invalid-credentials' };
    }

    try {
      const cred = await this.firebaseAuth.signIn(email, password);
      let profile: any;
      try {
        profile = await this.firebaseProfile.getUsuarioAdmin(cred.user.uid);
      } catch {
        await this.firebaseAuth.signOut();
        this.loggedInSubject.next(false);
        this.projectContext.clear();
        return { ok: false, messageKey: 'login.errors.not-authorized' };
      }

      if (!profile) {
        await this.firebaseAuth.signOut();
        this.loggedInSubject.next(false);
        this.projectContext.clear();
        return { ok: false, messageKey: 'login.errors.not-authorized' };
      }

      const profileMail = typeof profile.mail === 'string' ? profile.mail.trim().toLowerCase() : '';
      if (profileMail && profileMail !== email.toLowerCase()) {
        await this.firebaseAuth.signOut();
        this.loggedInSubject.next(false);
        this.projectContext.clear();
        return { ok: false, messageKey: 'login.errors.not-authorized' };
      }

      if (profile && profile.activo === false) {
        await this.firebaseAuth.signOut();
        this.loggedInSubject.next(false);
        this.projectContext.clear();
        return { ok: false, messageKey: 'login.errors.inactive-user' };
      }

      const allowed = this.extractAllowedPlatformIds(profile);
      if (allowed.length === 0) {
        // Helpful diagnostics: the most common cause is a mismatched field name in USUARIOSADMIN.
        // Avoid logging secrets; this is only UID + profile keys/values.
        try {
          // eslint-disable-next-line no-console
          console.warn('[AUTH] UsuarioAdmin has no project assignments', {
            uid: cred.user.uid,
            email,
            profile
          });
        } catch {
          // ignore
        }

        await this.firebaseAuth.signOut();
        this.loggedInSubject.next(false);
        this.projectContext.clear();
        return { ok: false, messageKey: 'login.errors.no-projects' };
      }
      this.projectContext.setAllowedPlatformIds(allowed);
      this.loggedInSubject.next(true);
      return { ok: true };
    } catch (err: any) {
      this.loggedInSubject.next(false);
      return { ok: false, messageKey: this.mapFirebaseAuthErrorToMessageKey(err) };
    }
  }

  async logout(): Promise<void> {
    if ((environment as any).authProvider === 'demo') {
      safeLocalStorageRemoveItem(this.DEMO_STORAGE_KEY);
      this.loggedInSubject.next(false);
      this.projectContext.clear();
      this.router.navigate(['/login']);
      return;
    }

    await this.firebaseAuth.signOut();
    this.projectContext.clear();
    this.router.navigate(['/login']);
  }

  /**
   * Synchronous check used by some places. In firebase mode it can be `false`
   * briefly until auth is initialized; use `isLoggedOnce$()` in guards.
   */
  isLogged(): boolean {
    const v = this.loggedInSubject.value;
    return v === true;
  }

  isLoggedOnce$(): Observable<boolean> {
    return this.isLoggedIn$.pipe(
      filter((v): v is boolean => typeof v === 'boolean'),
      map((v) => v)
    );
  }

  private extractAllowedPlatformIds(profile: any): string[] {
    if (!profile) {
      return [];
    }

    const ids =
      // Preferred/known variants.
      (profile as any).idsplataformas ??
      (profile as any).idsPlataformas ??
      // Some deployments store the allowlist directly in `idplataforma`.
      (profile as any).idplataforma ??
      (profile as any).idPlataforma ??
      // Legacy typos.
      (profile as any).idsplataforma ??
      (profile as any).idsPlataforma;

    if (Array.isArray(ids)) {
      return ids.map((v) => (typeof v === 'string' ? v : String(v))).filter((v) => v.trim().length > 0);
    }
    if (typeof ids === 'string' && ids.trim().length > 0) {
      // Support comma-separated values just in case.
      if (ids.includes(',')) {
        return ids.split(',').map((x: string) => x.trim()).filter(Boolean);
      }
      return [ids];
    }

    if (ids && typeof ids === 'object') {
      // Some implementations store an allowlist as a map: { areaspjccm01: true, ... }
      const keys = Object.keys(ids as Record<string, any>);
      const allowed = keys.filter((k) => Boolean((ids as any)[k]));
      if (allowed.length > 0) {
        return allowed;
      }
    }

    return [];
  }

  private mapFirebaseAuthErrorToMessageKey(error: any): string {
    const code = typeof error?.code === 'string' ? error.code : '';
    switch (code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'login.errors.invalid-credentials';
      case 'auth/invalid-email':
        return 'login.errors.invalid-email';
      case 'auth/network-request-failed':
        return 'login.errors.network';
      case 'auth/too-many-requests':
        return 'login.errors.too-many-requests';
      default:
        return 'login.errors.unknown';
    }
  }

  private storeLastAuthError(messageKey: string): void {
    safeLocalStorageSetItem(this.LAST_ERROR_STORAGE_KEY, messageKey);
  }

  consumeLastAuthError(): string | null {
    const key = safeLocalStorageGetItem(this.LAST_ERROR_STORAGE_KEY);
    if (!key) {
      return null;
    }
    safeLocalStorageRemoveItem(this.LAST_ERROR_STORAGE_KEY);
    return key;
  }
}
